import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://lavernaibackend.onrender.com/api';

export interface CAPEQuestion {
  id: string;
  type: 'mcq' | 'true_false' | 'fill_blank' | 'short_answer' | 'essay';
  question: string;
  options?: string[];
  correct_answer: string;
  explanation: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Expert';
  skills: string[];
  tags: string[];
  metadata?: any;
}

export interface CAPEContent {
  content_id: string;
  taxonomy_path: {
    category: string;
    subcategory: string;
    module: string;
    submodule: string;
    topic: string;
    subtopic: string;
  };
  difficulty: 'Beginner' | 'Intermediate' | 'Expert';
  skills: string[];
  tags: string[];
  source: string;
  content: {
    type: 'article' | 'text' | 'faq' | 'mcq' | 'video' | 'image';
    text: string;
    summary?: string;
    metadata?: any;
  };
}

export interface CAPERetrievalResult {
  content: CAPEContent[];
  questions: CAPEQuestion[];
  total_available: number;
  excluded_count: number;
}

export interface CAPEChallengeRequest {
  subtopic: string;
  difficulty?: 'Beginner' | 'Intermediate' | 'Expert';
  count?: number;
  questionTypes?: string[];
  kenyaContext?: boolean;
  competitorId: string;
  challengeId?: string;
}

export interface CAPEIngestionRequest {
  pageTitle: string;
  suggestedSubtopic?: string;
  overrides?: {
    difficulty?: string;
    tags?: string[];
    skills?: string[];
  };
}

export interface TaxonomyNode {
  category: string;
  subcategory: string;
  module: string;
  submodule: string;
  topic: string;
  subtopic: string;
  metadata: {
    difficulty_default?: string;
    skills?: string[];
    tags?: string[];
    prerequisites?: string[];
    learning_objectives?: string[];
  };
  fullPath: string;
}

class CAPEService {
  private baseURL: string;

  constructor() {
    this.baseURL = `${API_BASE_URL}/cape`;
  }

  /**
   * Initialize CAPE service
   */
  async initialize(): Promise<{ success: boolean; message: string }> {
    try {
      const response = await axios.post(`${this.baseURL}/initialize`);
      return response.data;
    } catch (error) {
      console.error('Error initializing CAPE:', error);
      throw new Error('Failed to initialize CAPE service');
    }
  }

  /**
   * Get content and questions for a competitor challenge
   */
  async getContentForChallenge(request: CAPEChallengeRequest): Promise<CAPERetrievalResult> {
    try {
      const params = new URLSearchParams({
        subtopic: request.subtopic,
        userId: request.competitorId,
        excludeSeen: 'true',
        count: (request.count || 10).toString(),
        questionTypes: (request.questionTypes || ['mcq', 'true_false']).join(','),
        kenyaContext: (request.kenyaContext !== false).toString()
      });

      if (request.difficulty) {
        params.append('difficulty', request.difficulty);
      }

      const response = await axios.get(`${this.baseURL}/content/retrieve?${params}`);
      return response.data.data;
    } catch (error) {
      console.error('Error getting content for challenge:', error);
      throw new Error('Failed to get content for challenge');
    }
  }

  /**
   * Search for content by query
   */
  async searchContent(query: string, filters: {
    category?: string;
    difficulty?: string;
    source?: string;
    limit?: number;
  } = {}): Promise<CAPEContent[]> {
    try {
      const params = new URLSearchParams({ query });
      
      if (filters.category) params.append('category', filters.category);
      if (filters.difficulty) params.append('difficulty', filters.difficulty);
      if (filters.source) params.append('source', filters.source);
      if (filters.limit) params.append('limit', filters.limit.toString());

      const response = await axios.get(`${this.baseURL}/content/search?${params}`);
      return response.data.data;
    } catch (error) {
      console.error('Error searching content:', error);
      throw new Error('Failed to search content');
    }
  }

  /**
   * Search taxonomy for topics
   */
  async searchTaxonomy(query: string, limit: number = 10): Promise<TaxonomyNode[]> {
    try {
      const params = new URLSearchParams({
        query,
        limit: limit.toString()
      });

      const response = await axios.get(`${this.baseURL}/taxonomy/search?${params}`);
      return response.data.data;
    } catch (error) {
      console.error('Error searching taxonomy:', error);
      throw new Error('Failed to search taxonomy');
    }
  }

  /**
   * Get taxonomy node by subtopic
   */
  async getTaxonomyBySubtopic(subtopic: string): Promise<TaxonomyNode | null> {
    try {
      const response = await axios.get(`${this.baseURL}/taxonomy/subtopic/${encodeURIComponent(subtopic)}`);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      console.error('Error getting taxonomy by subtopic:', error);
      throw new Error('Failed to get taxonomy by subtopic');
    }
  }

  /**
   * Get all categories
   */
  async getCategories(): Promise<string[]> {
    try {
      const response = await axios.get(`${this.baseURL}/taxonomy/categories`);
      return response.data.data;
    } catch (error) {
      console.error('Error getting categories:', error);
      throw new Error('Failed to get categories');
    }
  }

  /**
   * Get subcategories for a category
   */
  async getSubcategories(category: string): Promise<string[]> {
    try {
      const response = await axios.get(`${this.baseURL}/taxonomy/categories/${encodeURIComponent(category)}/subcategories`);
      return response.data.data;
    } catch (error) {
      console.error('Error getting subcategories:', error);
      throw new Error('Failed to get subcategories');
    }
  }

  /**
   * Ingest content from Wikipedia
   */
  async ingestFromWikipedia(request: CAPEIngestionRequest): Promise<{
    success: boolean;
    content_id?: string;
    action: 'created' | 'updated' | 'skipped';
    message: string;
    warnings?: string[];
  }> {
    try {
      const response = await axios.post(`${this.baseURL}/ingest/wikipedia`, request);
      return response.data;
    } catch (error) {
      console.error('Error ingesting from Wikipedia:', error);
      throw new Error('Failed to ingest content from Wikipedia');
    }
  }

  /**
   * Get content statistics
   */
  async getContentStats(): Promise<any> {
    try {
      const response = await axios.get(`${this.baseURL}/content/stats`);
      return response.data.data;
    } catch (error) {
      console.error('Error getting content stats:', error);
      throw new Error('Failed to get content statistics');
    }
  }

  /**
   * Get taxonomy statistics
   */
  async getTaxonomyStats(): Promise<any> {
    try {
      const response = await axios.get(`${this.baseURL}/taxonomy/stats`);
      return response.data.data;
    } catch (error) {
      console.error('Error getting taxonomy stats:', error);
      throw new Error('Failed to get taxonomy statistics');
    }
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<any> {
    try {
      const response = await axios.get(`${this.baseURL}/health`);
      return response.data.data;
    } catch (error) {
      console.error('Error checking CAPE health:', error);
      throw new Error('Failed to check CAPE health');
    }
  }

  /**
   * Test Wikipedia API
   */
  async testWikipedia(pageTitle: string): Promise<{
    summary: any;
    keyFacts: string[];
  }> {
    try {
      const response = await axios.get(`${this.baseURL}/test/wikipedia?pageTitle=${encodeURIComponent(pageTitle)}`);
      return response.data.data;
    } catch (error) {
      console.error('Error testing Wikipedia API:', error);
      throw new Error('Failed to test Wikipedia API');
    }
  }

  /**
   * Get personalized content recommendations for a competitor
   */
  async getPersonalizedContent(competitorId: string, preferences: {
    categories?: string[];
    difficulty?: string;
    limit?: number;
  } = {}): Promise<CAPEContent[]> {
    try {
      const params = new URLSearchParams({
        userId: competitorId,
        subtopic: 'Personalized Content',
        excludeSeen: 'true',
        count: (preferences.limit || 5).toString(),
        questionTypes: ['mcq', 'true_false'].join(',')
      });

      if (preferences.difficulty) {
        params.append('difficulty', preferences.difficulty);
      }

      // If categories are specified, search for content in those categories
      if (preferences.categories && preferences.categories.length > 0) {
        const searchPromises = preferences.categories.map(category => 
          this.searchContent(category, { limit: 2 })
        );
        const results = await Promise.all(searchPromises);
        return results.flat();
      }

      // Otherwise, get general content
      const response = await axios.get(`${this.baseURL}/content/retrieve?${params}`);
      return response.data.data.content;
    } catch (error) {
      console.error('Error getting personalized content:', error);
      throw new Error('Failed to get personalized content');
    }
  }

  /**
   * Record content interaction for anti-repeat logic
   */
  async recordContentInteraction(competitorId: string, contentId: string, questionId?: string): Promise<void> {
    try {
      // This would typically be handled by the backend when content is served
      // But we can make a call to ensure the interaction is recorded
      await axios.post(`${this.baseURL}/content/interaction`, {
        competitorId,
        contentId,
        questionId,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      // Don't throw error for interaction recording failures
      console.warn('Failed to record content interaction:', error);
    }
  }

  /**
   * Set test profile data for a user (for testing purposes)
   */
  async setTestProfile(userId: string, profile: string): Promise<any> {
    try {
      const response = await axios.post(`${this.baseURL}/interest-based-cape/users/${userId}/set-test-profile`, {
        profile
      });
      return response.data;
    } catch (error) {
      console.error('Error setting test profile:', error);
      throw new Error('Failed to set test profile');
    }
  }

  /**
   * Test Wikipedia API with specific user profile
   */
  async testWikipediaWithProfile(profile: string): Promise<any> {
    try {
      const response = await axios.post(`${this.baseURL}/test/wikipedia-profile`, {
        profile
      });
      return response.data;
    } catch (error) {
      console.error('Error testing Wikipedia with profile:', error);
      throw new Error('Failed to test Wikipedia API with profile');
    }
  }

  /**
   * Process user interest update and fetch personalized content
   */
  async processUserInterestUpdate(userId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await axios.post(`${this.baseURL}/interest-based-cape/users/${userId}/interests/process`);
      return response.data;
    } catch (error) {
      console.error('Error processing user interest update:', error);
      
      // If it's a 404 error, the endpoint might not be available yet
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        console.warn('Interest-based CAPE endpoint not available, skipping user interest processing');
        return { success: true, message: 'Interest processing skipped - endpoint not available' };
      }
      
      throw new Error('Failed to process user interest update');
    }
  }

  /**
   * Update user's CAPE content when profile changes
   */
  async updateUserCAPEContent(userId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await axios.put(`${this.baseURL}/interest-based-cape/users/${userId}/cape-content`);
      return response.data;
    } catch (error) {
      console.error('Error updating user CAPE content:', error);
      
      // If it's a 404 error, the endpoint might not be available yet
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        console.warn('Interest-based CAPE endpoint not available, skipping content update');
        return { success: true, message: 'Content update skipped - endpoint not available' };
      }
      
      throw new Error('Failed to update user CAPE content');
    }
  }

  /**
   * Get user's interest profile for CAPE
   */
  async getUserInterestProfile(userId: string): Promise<any> {
    try {
      const response = await axios.get(`${this.baseURL}/interest-based-cape/users/${userId}/interest-profile`);
      return response.data.data;
    } catch (error) {
      console.error('Error getting user interest profile:', error);
      
      // If it's a 404 error, the endpoint might not be available yet
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        console.warn('Interest-based CAPE endpoint not available, returning empty profile');
        return { interests: [], preferences: {} };
      }
      
      throw new Error('Failed to get user interest profile');
    }
  }
}

// Export singleton instance
const capeService = new CAPEService();
export default capeService;
